
import { useEffect, useState } from 'react';
import { Lightbulb, ArrowLeft, AlertTriangle, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import type { AnalysisResult } from '@/types/analysis';

interface CoachingInsight {
  category: string;
  needsImprovement: {
    originalQuote: string;
    betterApproach: string;
    explanation: string;
  }[];
  toFix: string[];
}

const Coaching = () => {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState<CoachingInsight[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          navigate('/');
          return;
        }

        const { data: analyses, error } = await supabase
          .from('call_analyses')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) throw error;

        if (analyses && analyses.length > 0) {
          const latestAnalysis = analyses[0];
          const feedbackData = latestAnalysis.feedback as unknown as AnalysisResult['feedback'];
          
          if (feedbackData?.feedback) {
            const formattedInsights = feedbackData.feedback
              .filter(category => 
                category.needsImprovement.length > 0 || category.toFix.length > 0
              )
              .map(category => ({
                category: category.category,
                needsImprovement: category.needsImprovement,
                toFix: category.toFix
              }));

            setInsights(formattedInsights);
          }
        }
      } catch (error) {
        console.error('Error fetching insights:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [navigate]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex items-center gap-4">
        <Link to="/">
          <Button variant="ghost" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </Link>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Lightbulb className="h-6 w-6" />
          Coaching Insights
        </h1>
      </div>

      {insights.length === 0 ? (
        <Card className="p-6 text-center text-gray-500">
          No coaching insights available yet. Complete a call analysis to get personalized feedback.
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {insights.map((insight, index) => (
            <Card key={index} className="p-6">
              <h3 className="font-semibold text-xl mb-4 text-primary">{insight.category}</h3>
              
              {insight.needsImprovement.length > 0 && (
                <div className="mb-6">
                  <h4 className="flex items-center gap-2 font-medium text-amber-600 mb-3">
                    <AlertTriangle className="h-5 w-5" />
                    Areas for Improvement
                  </h4>
                  <div className="space-y-4 pl-4">
                    {insight.needsImprovement.map((item, idx) => (
                      <div key={idx} className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                        <p className="text-gray-700 italic mb-2">"{item.originalQuote}"</p>
                        <p className="text-green-700 italic mb-2">Better approach: "{item.betterApproach}"</p>
                        <p className="text-sm text-gray-600">{item.explanation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {insight.toFix.length > 0 && (
                <div>
                  <h4 className="flex items-center gap-2 font-medium text-blue-600 mb-3">
                    <Wrench className="h-5 w-5" />
                    Action Items
                  </h4>
                  <ul className="space-y-2 pl-4">
                    {insight.toFix.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <span className="text-blue-500 mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Coaching;
